import memoizeOne from 'memoize-one';
import * as React from 'react';
import { shallowEqualObjects } from 'shallow-equal';

import {
    createFilteredPalette,
    type PaletteCommandDef,
    type PaletteDef,
    PlainMarkdown,
    PortalAbsolute,
    usePaletteHandle,
} from '@gitbook/react-uikit';
import { type Document, TextRange } from '@gitbook/slate';
import { findDOMNode } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    KeyboardShortcut,
    LoadingIndicator,
    ScrollFader,
    useScrollFaderController,
} from '@gitbook/spine-react';
import { useEventCallback, useLiveRef, useScrollListener } from '@gitbook/util-react-hooks';
import { assertIsDefined, assertNever } from '@gitbook/util-ts';

import { isBackspace } from '../common';
import { ContentEditorContext } from '../contexts';
import {
    type CompletionApi,
    type CompletionHistory,
    type CompletionOperation,
    type CompletionSelection,
    type CompletionState,
    type RetryTrigger,
    type RunTrigger,
    useCompletionApi,
    useCompletionState,
} from './CompletionContext';
import styles from './CompletionPrompt.module.css';

const PROMPT_GUTTER = 4;
const PROMPT_PREVIEW_MAX_HEIGHT = 250;
const PROMPT_SUGGESTIONS = ['Explain', 'Write a paragraph about', 'Write some code that'];
const LANGUAGES = [
    'English',
    'Korean',
    'Chinese',
    'Japanese',
    'Spanish',
    'Russian',
    'French',
    'Portuguese',
    'German',
    'Italian',
    'Dutch',
    'Indonesian',
    'Filipino',
    'Vietnamese',
];
const TONES = ['Professional', 'Casual', 'Straightforward', 'Confident', 'Friendly'];

type SubmitHandler = (args: { prompt: string; trigger: RunTrigger }) => void;
type RetryHandler = (args: { trigger: RetryTrigger }) => string | null;
type RetryHandlerVoid = (args: { trigger: RetryTrigger }) => void;

function createGenerateFromCommands(args: { onSubmit: (text: string) => void }) {
    const commands: PaletteCommandDef[] = [
        {
            key: 'summarize',
            title: 'Summarize',
            icon: Icon.AIGenerate,
            trigger: () => {
                args.onSubmit('Summarize');
            },
        },
        {
            key: 'explain-this',
            title: 'Explain this',
            icon: Icon.HelpCircle,
            trigger: () => {
                args.onSubmit('Explain this');
            },
        },
        {
            key: 'translate',
            title: 'Translate',
            icon: Icon.AITranslate,
            descendant: () =>
                ({
                    groups: [
                        {
                            key: 'translate-root',
                            commands: LANGUAGES.map((lang) => ({
                                key: `translate-${lang.toLowerCase()}`,
                                title: lang,
                                trigger: () => {
                                    args.onSubmit(`Translate in ${lang}`);
                                },
                            })),
                        },
                    ],
                    query: { text: '' },
                }) as PaletteDef,
        },
    ];
    return commands;
}

/**
 * Create a palette for an "insert" operation.
 */
function createInsertPalette(args: {
    onSubmit: (text: string) => void;
    onSuggest: (text: string) => void;
    query: string;
}) {
    const { onSubmit, onSuggest, query } = args;
    return createFilteredPalette(
        {
            groups: [
                {
                    key: 'write-with-ai',
                    title: 'Write with AI',
                    commands: [
                        {
                            key: 'continue-writing',
                            title: 'Continue writing',
                            icon: Icon.AIContinueWriting,
                            trigger: () => {
                                onSubmit('Continue writing');
                            },
                        },
                        ...PROMPT_SUGGESTIONS.map((suggestion, index) => ({
                            key: `prompt-suggestion-${index}`,
                            icon: Icon.AIStartPrompt,
                            title: `${suggestion}...`,
                            trigger: () => {
                                onSuggest(`${suggestion} `);
                            },
                        })),
                    ],
                },
                {
                    key: 'generate-from-page',
                    title: 'Generate from page',
                    commands: createGenerateFromCommands({ onSubmit }),
                },
            ],
        },
        query,
        { threshold: 0 }
    );
}

/**
 * Create a palette for a "replace" operation.
 */
function createReplacePalette(args: { onSubmit: (text: string) => void; query: string }) {
    const { onSubmit, query } = args;
    return createFilteredPalette(
        {
            groups: [
                {
                    key: 'edit-or-review-selection',
                    title: 'Edit or review selection',
                    commands: [
                        {
                            key: 'improve-writing',
                            title: 'Improve writing',
                            icon: Icon.AIImproveWriting,
                            trigger: () => {
                                onSubmit('Improve writing');
                            },
                        },
                        {
                            key: 'fix-spelling-and-grammar',
                            title: 'Fix spelling and grammar',
                            icon: Icon.AIFixSpellingGrammar,
                            trigger: () => {
                                onSubmit('Fix spelling and grammar');
                            },
                        },
                        {
                            key: 'make-shorter',
                            title: 'Make shorter',
                            icon: Icon.AIMakeShorter,
                            trigger: () => {
                                onSubmit('Make shorter');
                            },
                        },
                        {
                            key: 'make-longer',
                            title: 'Make longer',
                            icon: Icon.AIMakeLonger,
                            trigger: () => {
                                onSubmit('Make longer');
                            },
                        },
                        {
                            key: 'simplify-language',
                            title: 'Simplify language',
                            icon: Icon.AISimplifyLanguage,
                            trigger: () => {
                                onSubmit('Simplify language');
                            },
                        },
                        {
                            key: 'change-tone',
                            title: 'Change tone',
                            icon: Icon.AIChangeTone,
                            descendant: () =>
                                ({
                                    groups: [
                                        {
                                            key: 'change-tone-root',
                                            commands: TONES.map((tone) => ({
                                                key: `change-tone-to-${tone.toLowerCase()}`,
                                                title: tone,
                                                trigger: () => {
                                                    onSubmit(`Change tone to ${tone}`);
                                                },
                                            })),
                                        },
                                    ],
                                    query: { text: '' },
                                }) as PaletteDef,
                        },
                    ],
                },
                {
                    key: 'generate-from-selection',
                    title: 'Generate from selection',
                    commands: createGenerateFromCommands({ onSubmit }),
                },
            ],
        },
        query,
        { threshold: 0 }
    );
}

/**
 * Create a palette for an "insert" operation when the completion is done.
 */
function createDoneInsertPalette(args: {
    query: string;
    onRetry: () => void;
    onDone: () => void;
    onSubmit: (text: string) => void;
}) {
    const { query, onDone, onRetry, onSubmit } = args;
    return createFilteredPalette(
        {
            groups: [
                {
                    key: 'actions',
                    commands: [
                        {
                            key: 'continue-writing',
                            title: 'Continue writing',
                            icon: Icon.AIContinueWriting,
                            trigger: () => {
                                onSubmit('Continue writing');
                            },
                        },
                        {
                            key: 'make-shorter',
                            title: 'Make shorter',
                            icon: Icon.AIMakeShorter,
                            trigger: () => {
                                onSubmit('Make shorter');
                            },
                        },
                        {
                            key: 'make-longer',
                            title: 'Make longer',
                            icon: Icon.AIMakeLonger,
                            trigger: () => {
                                onSubmit('Make longer');
                            },
                        },
                    ],
                },
                {
                    key: 'actions',
                    commands: [
                        {
                            key: 'done',
                            title: 'Done',
                            icon: Icon.Check,
                            trigger: onDone,
                        },
                        {
                            key: 'try-again',
                            title: 'Try again',
                            icon: Icon.Reset,
                            trigger: onRetry,
                        },
                    ],
                },
            ],
        },
        query,
        { threshold: 0 }
    );
}

/**
 * Create a palette for a "replace" operation when the completion is done.
 */
function createDoneReplacePalette(args: {
    query: string;
    onReplaceSelection: () => void;
    onInsertBelowSelection: () => void;
    onRetry: () => void;
    onDiscard: () => void;
}) {
    const { query, onReplaceSelection, onInsertBelowSelection, onDiscard, onRetry } = args;
    return createFilteredPalette(
        {
            groups: [
                {
                    key: 'actions',
                    title: 'Actions',
                    commands: [
                        {
                            key: 'replace-selection',
                            title: 'Replace selection',
                            icon: Icon.AIReplaceSelection,
                            trigger: onReplaceSelection,
                        },
                        {
                            key: 'insert-below',
                            title: 'Insert below',
                            icon: Icon.AIInsertBelow,
                            trigger: onInsertBelowSelection,
                        },
                        {
                            key: 'try-again',
                            title: 'Try again',
                            icon: Icon.Reset,
                            trigger: onRetry,
                        },
                        {
                            key: 'discard',
                            title: 'Discard',
                            icon: Icon.AIDiscard,
                            trigger: onDiscard,
                        },
                    ],
                },
            ],
        },
        query,
        { threshold: 0 }
    );
}

/**
 * Handle the palette attached to the prompt.
 */
function usePromptPalette(args: {
    textareaRef: React.RefObject<HTMLTextAreaElement>;
    status: CompletionState['status'];
    operation: CompletionState['operation'];
    prompt: string;
    onSubmit: SubmitHandler;
    onPromptChange: (prompt: string) => void;
    onReplaceSelection: () => void;
    onInsertBelowSelection: () => void;
    onDiscard: () => void;
    onDone: () => void;
    onRetry: RetryHandlerVoid;
}) {
    const { onReplaceSelection, onInsertBelowSelection, onDiscard, onDone, onRetry } = args;
    const { open: openPalette, close: closePalette } = usePaletteHandle(args.textareaRef, {
        onClose: () => {
            args.textareaRef.current?.focus();
        },
    });
    const { logAnalyticsEvent } = React.useContext(ContentEditorContext);
    const submitFromPalette = useEventCallback((prompt: string) => {
        args.onSubmit({ prompt, trigger: 'palette-command' });
    });
    const setPromptFromPalette = useEventCallback((prompt: string) => {
        logAnalyticsEvent({
            type: 'edit_pick_ai_completion_palette',
            prompt,
        });
        args.onPromptChange(prompt);
    });

    const openPromptPalette = useEventCallback(() => {
        const palette = (() => {
            assertIsDefined(args.operation);
            if (args.status === 'done') {
                switch (args.operation) {
                    case 'insert':
                        return createDoneInsertPalette({
                            query: args.prompt,
                            onRetry: () => onRetry({ trigger: 'palette-command' }),
                            onDone,
                            onSubmit: submitFromPalette,
                        });
                    case 'replace':
                        return createDoneReplacePalette({
                            query: args.prompt,
                            onReplaceSelection,
                            onInsertBelowSelection,
                            onRetry: () => onRetry({ trigger: 'palette-command' }),
                            onDiscard,
                        });
                    default:
                        assertNever(args.operation);
                }
            }

            switch (args.operation) {
                case 'insert':
                    return createInsertPalette({
                        onSubmit: submitFromPalette,
                        onSuggest: setPromptFromPalette,
                        query: args.prompt,
                    });
                case 'replace':
                    return createReplacePalette({
                        onSubmit: submitFromPalette,
                        query: args.prompt,
                    });
                default:
                    assertNever(args.operation);
            }
        })();

        if (palette.groups.length === 0) {
            closePalette();
            return;
        }

        openPalette(palette);
    });

    // Manage the display of the palette
    React.useEffect(() => {
        if (args.status === 'running') {
            closePalette();
            return;
        }

        if (PROMPT_SUGGESTIONS.some((suggestion) => args.prompt.startsWith(suggestion))) {
            closePalette();
            return;
        }

        openPromptPalette();
    }, [args.status, args.prompt, args.operation, closePalette, openPromptPalette]);

    return { openPromptPalette };
}

const ShortcutIndicator = React.memo(function ShortcutIndicator(props: {
    label: string;
    shortcut: string;
}) {
    return (
        <div className="flex shrink-0 gap-1 text-secondary" style={{ padding: '9px 12px' }}>
            {props.label} <KeyboardShortcut shortcut={props.shortcut} style={{ opacity: 0.6 }} />
        </div>
    );
});

const HistoryNavigation = React.memo(function HistoryNavigation(props: {
    history: CompletionHistory;
    index: number;
    onNavigate: (index: number) => void;
}) {
    return (
        <div className="flex">
            <div className="mx-3 border-l" />
            <div className="flex items-center gap-1">
                <Button
                    size="small"
                    label="Previous"
                    kind="blank"
                    leadingIcon={Icon.ArrowLeft}
                    iconOnly
                    disabled={props.index === 0}
                    onClick={(event) => {
                        event.stopPropagation();
                        props.onNavigate(props.index - 1);
                    }}
                />
                <div className="text-secondary text-ui-small">
                    {props.index + 1} of {props.history.length}
                </div>
                <Button
                    size="small"
                    label="Next"
                    kind="blank"
                    leadingIcon={Icon.ArrowRight}
                    iconOnly
                    disabled={props.index === props.history.length - 1}
                    onClick={(event) => {
                        event.stopPropagation();
                        props.onNavigate(props.index + 1);
                    }}
                />
            </div>
        </div>
    );
});

const PromptInput = React.memo(function PromptInput(props: {
    onExit: () => void;
    onSubmit: SubmitHandler;
    prompt: string;
    onPromptChange: (prompt: string) => void;
    running: boolean;
    textareaRef: React.RefObject<HTMLTextAreaElement>;
    placeholder: string;
    history: CompletionHistory;
    historyIndex: number | null;
    onNavigate: (index: number) => void;
}) {
    const {
        running,
        onSubmit,
        onExit,
        textareaRef,
        prompt,
        onPromptChange,
        placeholder,
        history,
        historyIndex,
        onNavigate,
    } = props;

    return (
        <div className="relative flex items-end px-2">
            <Icon.Magic className={styles.icon} />
            <div className={styles['grow-wrap']} data-prompt={prompt}>
                <textarea
                    ref={textareaRef}
                    rows={1}
                    className={styles.textarea}
                    value={running ? '' : prompt}
                    disabled={running}
                    placeholder={running ? 'AI is writing...' : placeholder}
                    onKeyDown={(event) => {
                        if (
                            event.key === 'Escape' ||
                            // Backspace in an empty completion prompt will exit the prompt.
                            (isBackspace(event) && prompt.length === 0)
                        ) {
                            event.preventDefault();
                            onExit();
                        }
                        if (event.key === 'Enter' && !event.shiftKey) {
                            event.preventDefault();
                            if (prompt) {
                                onSubmit({ prompt, trigger: 'keyboard-enter' });
                            }
                        }
                    }}
                    onChange={(event) => {
                        onPromptChange(event.target.value);
                    }}
                />
            </div>

            {running ? (
                <>
                    <ShortcutIndicator label="Try again" shortcut="ALT + R" />
                    <ShortcutIndicator label="Stop" shortcut="ESC" />
                    <div className="flex shrink-0 px-1 py-3 text-secondary">
                        <LoadingIndicator />
                    </div>
                </>
            ) : (
                <div className="mb-2 flex">
                    <Button
                        kind="primary"
                        size="small"
                        label="Submit prompt"
                        leadingIcon={Icon.ArrowUp}
                        iconOnly
                        onClick={() => {
                            onSubmit({ prompt, trigger: 'submit-button' });
                        }}
                        disabled={!prompt}
                    />
                    {history.length > 1 && historyIndex !== null && (
                        <HistoryNavigation
                            history={history}
                            index={historyIndex}
                            onNavigate={onNavigate}
                        />
                    )}
                </div>
            )}
        </div>
    );
});

const ResultPreview = React.memo(function ResultPreview(props: { input: string | Document }) {
    const containerRef = React.useRef<HTMLDivElement>(null);

    // Remove the code block delimiters from the result for better display.
    const input =
        typeof props.input === 'string'
            ? props.input.replace(/{% code.*?%}\n|{% endcode %}\n?/gs, '')
            : props.input;

    const controller = useScrollFaderController('vertical', containerRef);

    return (
        <div className="relative">
            <ScrollFader controller={controller}>
                <div
                    ref={containerRef}
                    className="flex max-h-[250px] overflow-y-auto whitespace-pre-wrap px-3 pt-2 pb-1"
                >
                    <PlainMarkdown style={{ minWidth: 0 }} input={input} allowBlock={() => true} />
                </div>
            </ScrollFader>
        </div>
    );
});

const PromptPlaceholders: Record<CompletionOperation, string> = {
    insert: 'Ask AI to write something...',
    replace: 'Ask AI to improve your writing...',
};

const Prompt = React.memo(function Prompt(props: {
    onExit: () => void;
    onStop: CompletionApi['stop'];
    onSubmit: SubmitHandler;
    onReplaceSelection: () => void;
    onInsertBelowSelection: () => void;
    onRetry: RetryHandler;
    status: CompletionState['status'];
    operation: NonNullable<CompletionState['operation']>;
    completionId: string;
    preview: string | Document | null;
    history: CompletionHistory;
    historyIndex: number | null;
    onNavigate: (index: number) => void;
}) {
    const {
        status,
        onExit,
        onSubmit,
        onReplaceSelection,
        onInsertBelowSelection,
        onRetry,
        completionId,
        preview,
        operation,
        history,
        historyIndex,
        onNavigate,
    } = props;
    const propsRef = useLiveRef(props);
    const running = status === 'running' || status === 'stopping';
    const textareaRef = React.useRef<HTMLTextAreaElement>(null);
    const [prompt, setPrompt] = React.useState('');

    const handleRetry: RetryHandlerVoid = useEventCallback((args) => {
        const prompt = onRetry(args);
        if (prompt) {
            setPrompt(prompt);
        }
    });

    // Empty prompt when done
    React.useEffect(() => {
        if (status === 'done') {
            setPrompt('');
        }
    }, [status]);

    // Focus prompt at start of a new completion or when status is done.
    React.useEffect(() => {
        if (completionId || status === 'done') {
            textareaRef.current?.focus();
        }
    }, [completionId, status]);

    // If running:
    // - Stop the completion when ESC is pressed.
    // - Retry the completion when ALT + R is pressed.
    React.useEffect(() => {
        const { onStop, onRetry } = propsRef.current;
        if (!running) {
            return;
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                onStop({ trigger: 'keyboard-escape' });
            }
            if (event.code === 'KeyR' && event.altKey) {
                event.preventDefault();
                event.stopPropagation();
                onRetry({ trigger: 'keyboard-alt-r' });
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [running, propsRef]);

    const { openPromptPalette } = usePromptPalette({
        textareaRef,
        status,
        prompt,
        onSubmit,
        onPromptChange: setPrompt,
        onReplaceSelection,
        onInsertBelowSelection,
        onDiscard: onExit,
        onDone: onExit,
        onRetry: handleRetry,
        operation,
    });

    return (
        <div
            contentEditable={false}
            className="flex flex-col rounded-md border bg-base shadow-elevated"
            onClick={() => {
                if (prompt === '' && !running) {
                    openPromptPalette();
                }
            }}
            onBlur={(event) => {
                const relatedTarget = event.relatedTarget as HTMLElement | null;
                const focusingInsidePrompt = relatedTarget
                    ? event.currentTarget.contains(relatedTarget)
                    : false;
                if (!running && !focusingInsidePrompt && relatedTarget !== null) {
                    onExit();
                }
            }}
        >
            {preview && <ResultPreview input={preview} />}
            <PromptInput
                onExit={onExit}
                onSubmit={onSubmit}
                prompt={prompt}
                onPromptChange={setPrompt}
                running={running}
                textareaRef={textareaRef}
                placeholder={PromptPlaceholders[operation]}
                history={history}
                historyIndex={historyIndex}
                onNavigate={onNavigate}
            />
        </div>
    );
});

/**
 * Position deps allow use to memoize the position computation.
 */
type PositionDeps = {
    // This is the only one required to compute the position.
    selection: CompletionSelection;
    // These others are deps, if they change, then the position could change.
    document: Document;
    scrollTop: number;
};

type Position = {
    top?: number;
    bottom?: number;
    left: number;
    width: number;
};

/**
 * Compute the position of the prompt.
 */
const computePosition = memoizeOne((deps: PositionDeps): Position | null => {
    const node = deps.document.getNode(deps.selection.endKey);

    // If the node doesn't exist, then we can't show the prompt.
    if (!node) {
        return null;
    }

    // Find the DOM node of the end of the selection.
    const domNode = findDOMNode(deps.selection.endKey);

    // If we can't find the DOM node, then we can't show the prompt.
    // It could happen if the block has been deleted.
    if (!domNode) {
        return null;
    }

    // Find the parent block of the selection.
    // To be able to get the correct horizontal position, we need to find the parent block.
    // Else with inline code we could have overflow issues.
    const fragmentOrDoc = deps.document.getClosestFragment(deps.selection.endKey) ?? deps.document;
    const parentBlock = fragmentOrDoc.getFurthestBlock(deps.selection.endKey);
    const parentDomNode = parentBlock ? findDOMNode(parentBlock) : domNode;

    if (!parentDomNode) {
        return null;
    }

    const clientRect = domNode.getBoundingClientRect();
    const parentClientRect =
        parentDomNode !== domNode ? parentDomNode.getBoundingClientRect() : clientRect;

    // Calculate the desired top position
    const desiredTop =
        TextRange.isTextRange(deps.selection.range) && deps.selection.range.isCollapsed
            ? clientRect.top
            : clientRect.bottom + PROMPT_GUTTER;

    // Estimate overlay height (approximate based on common overlay sizes)
    const estimatedOverlayHeight = PROMPT_PREVIEW_MAX_HEIGHT; // Reasonable estimate for the prompt overlay
    const viewportHeight = window.innerHeight;

    // Check if the overlay would extend beyond the bottom of the viewport
    const wouldOverflow = desiredTop + estimatedOverlayHeight > viewportHeight;

    if (wouldOverflow) {
        // Position with bottom instead of top
        const bottomPosition = viewportHeight - clientRect.top + PROMPT_GUTTER;
        return {
            bottom: bottomPosition,
            left: parentClientRect.left,
            width: parentClientRect.width,
        };
    }

    return {
        top: desiredTop,
        left: parentClientRect.left,
        width: parentClientRect.width,
    };
}, shallowEqualObjects);

/**
 * Completion prompt that keeps track of the current AI completion.
 */
export const PositionedPrompt = React.memo(function CompletionPrompt(props: {
    document: Document;
    completionState: CompletionState;
    completionApi: CompletionApi;
}) {
    const { completionState, completionApi } = props;
    const scrollTopRef = React.useRef(0);
    const [position, setPosition] = React.useState<null | Position>(null);

    const updatePosition = React.useCallback(() => {
        setPosition((prevPosition) => {
            if (!completionState.selection) {
                return null;
            }

            const position = computePosition({
                document: props.document,
                selection: completionState.selection,
                scrollTop: scrollTopRef.current,
            });

            // If the position hasn't changed, then we don't need to update.
            if (shallowEqualObjects(position, prevPosition)) {
                return prevPosition;
            }

            return position;
        });
    }, [completionState.selection, props.document]);

    React.useLayoutEffect(() => {
        updatePosition();

        // Update position every 500ms to ensure everything is up to date.
        // without requestIdleCallback, we don't do it, not a problem.
        if ('requestIdleCallback' in window) {
            let requestId: number;

            const intervalId = window.setInterval(() => {
                cancelIdleCallback(requestId);
                requestId = window.requestIdleCallback(updatePosition);
            }, 500);

            return () => {
                cancelIdleCallback(requestId);
                clearInterval(intervalId);
            };
        }
    }, [updatePosition]);

    const windowRef = React.useRef(window);
    useScrollListener(
        (event) => {
            scrollTopRef.current = (event.target as Element).scrollTop;
            updatePosition();
        },
        position ? windowRef : null,
        { capture: true }
    );

    const handleExit = useEventCallback(() => {
        completionApi.close();
    });

    const handleStop: CompletionApi['stop'] = useEventCallback((args) => {
        completionApi.stop(args);
    });

    const handleSubmit: SubmitHandler = useEventCallback((args) => {
        completionApi.run(args);
    });

    if (!position || !completionState.completionId || !completionState.operation) {
        return null;
    }

    const preview =
        completionState.operation === 'replace'
            ? (completionState.resultDocument ?? completionState.result)
            : null;

    return (
        <PortalAbsolute>
            <div
                className="absolute z-50"
                style={{
                    ...position,
                }}
            >
                <Prompt
                    onExit={handleExit}
                    onStop={handleStop}
                    onSubmit={handleSubmit}
                    status={completionState.status}
                    operation={completionState.operation}
                    completionId={completionState.completionId}
                    preview={preview}
                    onReplaceSelection={completionApi.replaceSelection}
                    onInsertBelowSelection={completionApi.insertBelowSelection}
                    onRetry={completionApi.retry}
                    history={completionState.history}
                    historyIndex={completionState.historyIndex}
                    onNavigate={completionApi.goToHistory}
                />
            </div>
        </PortalAbsolute>
    );
});

export function CompletionPrompt(props: { document: Document }) {
    const completionState = useCompletionState();
    const completionApi = useCompletionApi();

    if (!completionApi || !completionState) {
        return null;
    }

    if (!completionState.completionId || !completionState.operation) {
        return null;
    }

    return (
        <PositionedPrompt
            document={props.document}
            completionState={completionState}
            completionApi={completionApi}
        />
    );
}
